import { useQuery } from "@tanstack/react-query";
import type { ExpiringCertsInsight, OrgHealthInsight } from "@/hooks/use-dashboard-insights";
import { orgHealthKeys } from "@/lib/query";

// Types are co-located with the hook (per frontend.md); the widget insight
// shapes are shared with the home-dashboard hook they originated from.

/**
 * An `employment_type.key`. Not a union: employment types are admin-managed
 * config (EPIC-028), so a key added in Settings must be filterable without a
 * deploy. The server treats an unknown key as matching no rows.
 */
export type EmploymentTypeFilter = string;

export interface OrgHealthFilters {
  pillarId: string | null;
  departmentId: string | null;
  roleId: string | null;
  /** Empty = all employment types (no filter sent). */
  employmentTypes: EmploymentTypeFilter[];
}

export const EMPTY_ORG_HEALTH_FILTERS: OrgHealthFilters = {
  pillarId: null,
  departmentId: null,
  roleId: null,
  employmentTypes: [],
};

export interface OrgHealthOverview {
  orgHealth: OrgHealthInsight;
  expiringCerts: ExpiringCertsInsight;
}

export interface HeadcountPoint {
  /** `YYYY-MM`. */
  month: string;
  count: number;
  /** snapshot = exact nightly data; derived = reconstructed approximation. */
  source: "snapshot" | "derived";
}

/** The filter set as URL search params (shared by both endpoints + the URL bar). */
export function filterSearchParams(filters: OrgHealthFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.pillarId) params.set("pillarId", filters.pillarId);
  if (filters.departmentId) params.set("departmentId", filters.departmentId);
  if (filters.roleId) params.set("roleId", filters.roleId);
  if (filters.employmentTypes.length > 0) {
    params.set("employmentTypes", filters.employmentTypes.join(","));
  }
  return params;
}

export function useOrgHealthOverview(filters: OrgHealthFilters) {
  const qs = filterSearchParams(filters).toString();
  return useQuery({
    queryKey: orgHealthKeys.overview(qs),
    queryFn: async (): Promise<OrgHealthOverview> => {
      const res = await fetch(`/api/org-health${qs ? `?${qs}` : ""}`);
      if (!res.ok) throw new Error("Failed to load organization health");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useHeadcountTrend(filters: OrgHealthFilters, months: number) {
  const params = filterSearchParams(filters);
  params.set("months", String(months));
  const qs = params.toString();
  return useQuery({
    queryKey: orgHealthKeys.headcount(qs),
    queryFn: async (): Promise<{ points: HeadcountPoint[] }> => {
      const res = await fetch(`/api/org-health/headcount?${qs}`);
      if (!res.ok) throw new Error("Failed to load headcount trend");
      const json = await res.json();
      return json.data;
    },
  });
}
